import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2FormField,
  V2FormGrid,
  V2Modal,
} from '@sakhtar/design-system/react';
import { systemClock } from '@sakhtar/calendar';
import { ApiError, apiPost } from '../../lib/apiClient.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { formatRialFa, parseRialInput } from '../../lib/finance-display.js';
import type { FiscalPeriodListResponse } from '../../lib/types/finance.js';
import type { InvoiceDraft, SalesInvoice } from '../../lib/types/crm.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { useApiGet } from '../../lib/useApiGet.js';
import {
  allocateFifo,
  compareRational,
  issueLineAmounts,
  parseQuantity,
  parseRationalText,
  rationalText,
  sumBig,
} from './invoiceIssueModel.js';

/**
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — `/t/kimia/sales` (V2Modal + V2Card language);
 *   no dedicated frame exists for invoice issuing.
 * status: KIT_CONFORMANT, V2-only.
 *
 * «صدور فاکتور» from a sales order (ADR-013): only delivered, still-uninvoiced quantities can
 * be invoiced, so each line starts at the available quantity and can be lowered (partial
 * invoice) or given a discount. The panel at the bottom states exactly where the document
 * lands in finance — a DRAFT revenue journal plus the customer receivable — and, once
 * created, links straight to both. Totals are a preview; the server recomputes.
 */
export function InvoiceIssueDialog({
  base,
  orderRef,
  onClose,
  onCreated,
}: {
  base: string;
  orderRef: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { unit, label } = useAmountDisplay();
  const draft = useApiGet<InvoiceDraft>(`${base}/api/v1/sales/orders/${orderRef}/invoice-draft`, [
    base,
    orderRef,
  ]);
  const periods = useApiGet<FiscalPeriodListResponse>(`${base}/api/v1/accounting/fiscal-periods`, [
    base,
  ]);
  const taxes = useApiGet<MasterDataListResponse>(
    `${base}/api/v1/master-data/taxes?status=active&limit=200`,
    [base],
  );

  const [businessDate, setBusinessDate] = useState<string>(systemClock.today());
  const [taxId, setTaxId] = useState('');
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [discounts, setDiscounts] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<SalesInvoice | null>(null);

  const period =
    periods.status === 'ready'
      ? periods.data.items.find(
          (p) =>
            p.startDate <= businessDate && businessDate <= p.endDate && p.postingState === 'open',
        )
      : undefined;
  const tax =
    taxes.status === 'ready' ? taxes.data.items.find((t) => String(t.taxId) === taxId) : undefined;
  const taxRate = tax
    ? {
        numerator: BigInt(String(tax.rateNumerator)),
        denominator: BigInt(String(tax.rateDenominator)),
      }
    : null;

  const rows = useMemo(() => {
    if (draft.status !== 'ready') return [];
    return draft.data.lines
      .filter((line) => line.allocations.length > 0)
      .map((line) => {
        const typed = quantities[line.orderLineId];
        const available = parseRationalText(line.availableQuantity);
        const quantity = typed === undefined ? available : parseQuantity(typed);
        const discountText = discounts[line.orderLineId] ?? '';
        const discount = discountText === '' ? 0n : BigInt(parseRialInput(discountText) || '0');
        let rowError: string | null = null;
        if (quantity === null) rowError = 'مقدار معتبر نیست.';
        else if (compareRational(quantity, available) > 0)
          rowError = 'بیش از مقدار تحویل‌شدهٔ فاکتورنشده.';
        const amounts =
          quantity !== null && rowError === null
            ? issueLineAmounts(
                { quantity, unitPriceRial: BigInt(line.unitPriceRial), discountRial: discount },
                taxRate,
              )
            : null;
        if (amounts?.error) rowError = amounts.error;
        return { line, quantity, discount, amounts, rowError, available };
      });
  }, [draft, quantities, discounts, taxId, taxes]);

  const valid =
    rows.length > 0 &&
    rows.every((r) => r.rowError === null && r.amounts !== null) &&
    rows.every((r) => r.discount === 0n || (reasons[r.line.orderLineId] ?? '').trim() !== '');
  const taxable = sumBig(rows.map((r) => r.amounts?.taxableRial ?? 0n));
  const vat = sumBig(rows.map((r) => r.amounts?.vatRial ?? 0n));
  const total = taxable + vat;
  const needsVatAccount = vat > 0n;

  const ready = draft.status === 'ready' && periods.status === 'ready';
  // Finance owns where an invoice lands; the seller only sees it.
  const posting = draft.status === 'ready' ? draft.data.postingAccounts : null;
  const label_ = (ref: { code: string; name: string } | null | undefined) =>
    ref ? `${ref.code} — ${ref.name}` : 'تنظیم نشده';

  async function issue() {
    setError(null);
    if (!period) return setError('برای تاریخ انتخابی، دورهٔ مالی باز پیدا نشد.');
    if (!posting?.configured) {
      return setError('حساب‌های مقصد فاکتور هنوز توسط مالی تنظیم نشده است.');
    }
    if (needsVatAccount && !posting.vatAccountId) {
      return setError('حساب مالیات بر ارزش افزوده هنوز توسط مالی تنظیم نشده است.');
    }
    if (!valid) return setError('اقلام فاکتور را بررسی کنید.');
    const lines = [];
    for (const r of rows) {
      const spread = allocateFifo(r.line.allocations, r.quantity!);
      if (!spread) return setError('تخصیص تحویل‌ها ممکن نیست؛ مقدار را بررسی کنید.');
      lines.push({
        orderLineId: r.line.orderLineId,
        quantity: rationalText(r.quantity!),
        ...(r.discount > 0n
          ? {
              discountRial: r.discount.toString(),
              discountReason: (reasons[r.line.orderLineId] ?? '').trim(),
            }
          : {}),
        allocations: spread,
      });
    }
    setSaving(true);
    try {
      const invoice = await apiPost<SalesInvoice>(
        `${base}/api/v1/sales/orders/${orderRef}/invoices`,
        {
          businessDate,
          fiscalYearId: period.fiscalYearId,
          fiscalPeriodId: period.fiscalPeriodId,
          ...(taxId ? { taxId } : {}),
          lines,
        },
      );
      setCreated(invoice);
      onCreated();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.envelope.error.details?.[0]?.message ?? err.envelope.error.message)
          : 'صدور فاکتور ناموفق بود.',
      );
    } finally {
      setSaving(false);
    }
  }

  if (created) {
    return (
      <V2Modal
        open
        title="فاکتور صادر شد"
        onClose={onClose}
        footer={
          <V2Button variant="primary" onClick={onClose}>
            بستن
          </V2Button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <V2Alert tone="success">
            فاکتور به‌صورت پیش‌نویس صادر شد. سند درآمد و مطالبهٔ مشتری در مالی ساخته شد و پس از
            ارسال، تأیید و ثبت قطعی در دفاتر می‌نشیند.
          </V2Alert>
          <ul style={{ margin: 0, paddingInlineStart: 20, lineHeight: 2 }}>
            <li>
              <Link to={`${base}/crm/invoices/${created.invoiceId}`} onClick={onClose}>
                مشاهدهٔ فاکتور فروش
              </Link>
            </li>
            <li>
              <Link to={`${base}/finance/journals/${created.revenueJournalId}`} onClick={onClose}>
                سند درآمد (پیش‌نویس) در مالی
              </Link>
            </li>
            <li>
              <Link to={`${base}/finance/ar/${created.arOpenItemId}`} onClick={onClose}>
                مطالبه از مشتری (حساب دریافتنی)
              </Link>
            </li>
          </ul>
        </div>
      </V2Modal>
    );
  }

  return (
    <V2Modal
      open
      size="lg"
      title={`صدور فاکتور برای سفارش ${orderRef}`}
      onClose={onClose}
      dismissible={!saving}
      footer={
        <>
          <V2Button variant="ghost" onClick={onClose} disabled={saving}>
            انصراف
          </V2Button>
          <V2Button
            variant="primary"
            onClick={() => void issue()}
            disabled={saving || !ready || !valid}
          >
            {saving ? 'در حال صدور…' : 'صدور فاکتور'}
          </V2Button>
        </>
      }
    >
      {draft.status === 'loading' || periods.status === 'loading' ? <LoadingState /> : null}
      {draft.status === 'error' ? (
        <StatePanel
          variant={draft.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            draft.error.status === 403
              ? 'دسترسی صدور فاکتور ندارید'
              : 'اطلاعات صدور فاکتور در دسترس نیست'
          }
        />
      ) : null}
      {draft.status === 'ready' && !draft.data.invoiceable ? (
        <V2Alert tone="warn" title="چیزی برای فاکتور کردن نیست">
          فاکتور فروش فقط برای کالای تحویل‌شده صادر می‌شود. ابتدا موجودی را رزرو و تحویل را ثبت
          کنید، یا اگر همهٔ تحویل‌ها فاکتور شده‌اند، فاکتور بیشتری لازم نیست.
        </V2Alert>
      ) : null}

      {ready && draft.data.invoiceable ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error ? <V2Alert tone="danger">{error}</V2Alert> : null}
          <V2FormGrid>
            <V2FormField label="تاریخ فاکتور" width="medium">
              <JalaliDateInput value={businessDate} onChange={setBusinessDate} />
            </V2FormField>
            <V2FormField label="دورهٔ مالی" width="medium">
              <span>{period ? period.name : 'دورهٔ باز برای این تاریخ نیست'}</span>
            </V2FormField>
            <V2FormField label="مالیات" width="medium">
              <select
                className="sk-select"
                value={taxId}
                onChange={(e) => setTaxId(e.target.value)}
              >
                <option value="">— بدون مالیات —</option>
                {taxes.status === 'ready'
                  ? taxes.data.items.map((t) => (
                      <option key={String(t.taxId)} value={String(t.taxId)}>
                        {String(t.name ?? t.code)}
                      </option>
                    ))
                  : null}
              </select>
            </V2FormField>
          </V2FormGrid>

          <div className="sk-table-scroll">
            <table className="sk-table sk-table--compact">
              <thead>
                <tr>
                  <th>کالا</th>
                  <th>قابل فاکتور</th>
                  <th style={{ width: 110 }}>مقدار فاکتور</th>
                  <th>فی ({label})</th>
                  <th style={{ width: 120 }}>تخفیف ({label})</th>
                  <th style={{ width: 150 }}>علت تخفیف</th>
                  <th>جمع ({label})</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.line.orderLineId}>
                    <td>
                      {r.line.productName}
                      <div className="ltr" style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>
                        {r.line.productRef}
                      </div>
                    </td>
                    <td className="sk-num">
                      {r.line.availableQuantity} {r.line.uomRef}
                    </td>
                    <td>
                      <input
                        className="sk-input ltr sk-num"
                        style={{ width: '100%' }}
                        value={quantities[r.line.orderLineId] ?? rationalText(r.available)}
                        onChange={(e) =>
                          setQuantities((q) => ({ ...q, [r.line.orderLineId]: e.target.value }))
                        }
                        aria-invalid={r.rowError !== null}
                      />
                    </td>
                    <td className="sk-num">{formatRialFa(r.line.unitPriceRial, unit)}</td>
                    <td>
                      <input
                        className="sk-input ltr sk-num"
                        style={{ width: '100%' }}
                        value={discounts[r.line.orderLineId] ?? ''}
                        onChange={(e) =>
                          setDiscounts((d) => ({
                            ...d,
                            [r.line.orderLineId]: parseRialInput(e.target.value),
                          }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="sk-input"
                        style={{ width: '100%' }}
                        value={reasons[r.line.orderLineId] ?? ''}
                        disabled={r.discount === 0n}
                        onChange={(e) =>
                          setReasons((d) => ({ ...d, [r.line.orderLineId]: e.target.value }))
                        }
                      />
                    </td>
                    <td className="sk-num">
                      {r.rowError ? (
                        <span style={{ color: 'var(--sk-danger)', fontSize: 12 }}>
                          {r.rowError}
                        </span>
                      ) : r.amounts ? (
                        formatRialFa(r.amounts.totalRial.toString(), unit)
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <section
            aria-label="مقصد در مالی"
            style={{
              border: '1px solid var(--sk-border)',
              borderRadius: 'var(--sk-radius-md, 10px)',
              padding: 14,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <strong>مقصد این فاکتور در مالی</strong>
            {posting?.configured ? null : (
              <V2Alert tone="warn">
                حساب‌های مقصد فاکتور هنوز تنظیم نشده است. این تنظیم با تیم مالی است؛{' '}
                <Link to={`${base}/finance/sales-accounts`}>تنظیم حساب‌های فروش در مالی</Link>
              </V2Alert>
            )}
            <ul style={{ margin: 0, paddingInlineStart: 20, lineHeight: 2, fontSize: 13 }}>
              <li>
                سند درآمد <V2Badge tone="neutral">پیش‌نویس</V2Badge>: بدهکار «
                {label_(posting?.receivable)}»{' '}
                <b className="sk-num">{formatRialFa(total.toString(), unit)}</b> {label}
              </li>
              <li>
                بستانکار «{label_(posting?.revenue)}»{' '}
                <b className="sk-num">{formatRialFa(taxable.toString(), unit)}</b> {label}
              </li>
              {needsVatAccount ? (
                <li>
                  بستانکار «{label_(posting?.vat)}»{' '}
                  <b className="sk-num">{formatRialFa(vat.toString(), unit)}</b> {label}
                </li>
              ) : null}
              <li>
                مطالبه از {draft.data.partyName ?? draft.data.partyRef ?? 'مشتری'} در حساب‌های
                دریافتنی ثبت می‌شود؛ سررسید طبق شرایط پرداخت مشتری است.
              </li>
              <li>
                سند بعد از «ارسال برای تأیید ← تأیید ← ثبت قطعی» در دفاتر قرار می‌گیرد؛ تا آن زمان
                قابل اصلاح است و ثبت قطعی آن اصلاح‌ناپذیر (فقط با سند معکوس).
              </li>
            </ul>
          </section>
        </div>
      ) : null}
    </V2Modal>
  );
}
